import { z } from "zod";
import { PublicIdSchema } from "./common";

export const ExpenseType = z.enum(["highway", "parking", "other"]);
export const ExpensePaymentMethod = z.enum([
  "cash",
  "credit_card",
  "invoice",
  "accounts_receivable",
]);

export const DriverExpense = z.object({
  public_id: PublicIdSchema,
  expense_type: ExpenseType,
  payment_method: ExpensePaymentMethod,
  amount: z.number().int().min(0),
  description: z.string().nullable(),
  incurred_on: z.iso.date(),
  created_at: z.iso.datetime({ offset: true }),
  /** 領収書画像の presigned URL (未アップロードは null)。 */
  receipt_image_url: z.string().nullable(),
  /** #616: 経費が発生した配達先 (route_stop)。未紐付けは null。 */
  route_stop_public_id: PublicIdSchema.nullable(),
  /** #616: 配達先に紐付く注文。集荷 stop など注文がない場合は null。 */
  order_public_id: PublicIdSchema.nullable(),
  /** #616: 表示用の配達先名 (未紐付けは null)。 */
  stop_name: z.string().nullable(),
  /** #616: 表示用の受注番号 (未紐付け・未設定は null)。 */
  order_number: z.string().nullable(),
  /** #616: 事務による代理入力なら true (登録者がドライバー本人ではない)。 */
  entered_by_proxy: z.boolean(),
});

export const CreateExpenseInput = z.object({
  expense_type: ExpenseType,
  payment_method: ExpensePaymentMethod,
  amount: z.number().int().min(1),
  description: z.string().optional(),
  incurred_on: z.iso.date(),
  /**
   * #616: 経費が発生した配達先 (任意)。指定した場合、その stop の配送日と incurred_on が
   * 一致しない入力はサーバーが拒否する (どの日の経費か食い違うのを防ぐ)。
   */
  route_stop_public_id: PublicIdSchema.optional(),
});

export const UpdateExpenseInput = z
  .object({
    expense_type: ExpenseType.optional(),
    payment_method: ExpensePaymentMethod.optional(),
    amount: z.number().int().min(1).optional(),
    description: z.string().nullable().optional(),
    /** #616: 紐付け先の付け替え。null で解除。対象日 (incurred_on) は変えられない。 */
    route_stop_public_id: PublicIdSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (input) =>
      input.expense_type !== undefined ||
      input.payment_method !== undefined ||
      input.amount !== undefined ||
      input.description !== undefined ||
      input.route_stop_public_id !== undefined,
    {
      message: "更新項目を1つ以上指定してください",
    },
  );

export const ListDriverExpensesInput = z.object({
  /** 開始日 (含む). 未指定なら今月 1 日 */
  from: z.iso.date().optional(),
  /** 終了日 (含む). 未指定なら今日 */
  to: z.iso.date().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

/**
 * #616: 経費の紐付け先候補 (その日の配達先)。ETC のように後日金額が判明する経費を
 * 過去日の配達先へ後から登録できるよう、対象日を指定して取得する。
 */
export const ListExpenseStopOptionsInput = z.object({
  /** 対象日 (YYYY-MM-DD)。未指定は JST 本日。 */
  date: z.iso.date().optional(),
});

export const ExpenseStopOption = z.object({
  route_stop_public_id: PublicIdSchema,
  order_public_id: PublicIdSchema.nullable(),
  order_number: z.string().nullable(),
  /** 配達先の表示名 (取引先会社名 → 配送先名 → 住所 の順で解決)。 */
  name: z.string(),
  address: z.string().nullable(),
  /** ルート内の訪問順。 */
  sequence: z.number().int(),
  /** 配送日 (= 経費の incurred_on として使う値)。 */
  delivery_date: z.iso.date(),
  /** #616: この配達を担当するドライバー。注文詳細から代理登録するとき対象者の特定に使う。 */
  driver_public_id: PublicIdSchema.nullable(),
  driver_name: z.string().nullable(),
  /** 配達完了済みか。未完了でも選択できる (走行中に発生した高速代を先に登録できる)。 */
  completed: z.boolean(),
});

export type DriverExpenseDto = z.infer<typeof DriverExpense>;
export type CreateExpenseInputDto = z.infer<typeof CreateExpenseInput>;
export type UpdateExpenseInputDto = z.infer<typeof UpdateExpenseInput>;
export type ExpenseTypeDto = z.infer<typeof ExpenseType>;
export type ExpensePaymentMethodDto = z.infer<typeof ExpensePaymentMethod>;
export type ExpenseStopOptionDto = z.infer<typeof ExpenseStopOption>;
export type ListExpenseStopOptionsInputDto = z.infer<typeof ListExpenseStopOptionsInput>;
